import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter, ArrowUpDown, TrendingUp, Award, ShoppingCart, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) => {
  const n = Number(v || 0);
  if (n < 0) return `Rs. -${Math.abs(n).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return `Rs. ${n.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};
const getMonthRange = () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(new Date(n.getFullYear(), n.getMonth()+1, 0).getDate()).padStart(2,'0')}` }; };

const Profitability = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, rows: [], categories: [], chart_data: [] });
  const mr = getMonthRange();
  const [startDate, setStartDate] = useState(mr.start);
  const [endDate, setEndDate] = useState(mr.end);
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('gross_profit');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (category) params.set('category', category);
      params.set('sort_by', sortBy);
      const r = await api.get(`/reports/profitability?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (category) params.set('category', category);
    window.open(`${BACKEND_URL}/api/reports/profitability/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (category) params.set('category', category);
    window.open(`${BACKEND_URL}/api/reports/profitability/export-csv?${params}`, '_blank');
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  // Sort rows locally
  const sortedRows = [...(data.rows || [])].sort((a, b) => {
    const va = a[sortBy] || 0;
    const vb = b[sortBy] || 0;
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  const s = data.summary || {};
  const tierColor = (t) => {
    if (t === 'top') return 'bg-[#EFF6FF]';
    if (t === 'bottom') return 'bg-[#FEF2F2]';
    return '';
  };

  const SortHeader = ({ label, col, align }) => (
    <th className={`py-3 px-4 cursor-pointer hover:bg-[#F5F5F5] select-none ${align || 'text-left'}`} onClick={() => handleSort(col)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="w-3 h-3 text-[#9ca3af]" /></span>
    </th>
  );

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="profitability-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="profitability-title">Product Profitability</h1>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-sm text-sm" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Export PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-sm text-sm hover:bg-[#F5F5F5]" data-testid="export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="profitability-filters">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="start-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="end-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="category-filter">
                <option value="">All Categories</option>
                {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={fetchData} className="w-full flex items-center justify-center gap-2 bg-[#1a1a2e] text-white px-4 py-2 rounded-sm text-sm" data-testid="apply-filters-btn"><Filter className="w-4 h-4" />Apply</button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="most-profitable-card">
              <div className="flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-[#F59E0B]" /><p className="text-xs uppercase tracking-wider text-[#6b7280]">Most Profitable</p></div>
              {s.most_profitable ? (
                <><p className="font-semibold text-[#1a1a2e]">{s.most_profitable.name}</p><p className="text-sm text-[#1E40AF]">{fmtRs(s.most_profitable.amount)}</p></>
              ) : <p className="text-sm text-[#9ca3af]">No data</p>}
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="highest-margin-card">
              <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#1E40AF]" /><p className="text-xs uppercase tracking-wider text-[#6b7280]">Highest Margin</p></div>
              {s.highest_margin ? (
                <><p className="font-semibold text-[#1a1a2e]">{s.highest_margin.name}</p><p className="text-sm text-[#1E40AF]">{s.highest_margin.margin}%</p></>
              ) : <p className="text-sm text-[#9ca3af]">No data</p>}
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="best-selling-card">
              <div className="flex items-center gap-2 mb-2"><ShoppingCart className="w-4 h-4 text-[#1E40AF]" /><p className="text-xs uppercase tracking-wider text-[#6b7280]">Best Selling</p></div>
              {s.best_selling ? (
                <><p className="font-semibold text-[#1a1a2e]">{s.best_selling.name}</p><p className="text-sm text-[#6b7280]">{s.best_selling.quantity} units</p></>
              ) : <p className="text-sm text-[#9ca3af]">No data</p>}
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="least-profitable-card">
              <div className="flex items-center gap-2 mb-2"><AlertCircle className="w-4 h-4 text-[#EF4444]" /><p className="text-xs uppercase tracking-wider text-[#6b7280]">Least Profitable</p></div>
              {s.least_profitable ? (
                <><p className="font-semibold text-[#1a1a2e]">{s.least_profitable.name}</p><p className="text-sm text-[#EF4444]">{fmtRs(s.least_profitable.amount)}</p></>
              ) : <p className="text-sm text-[#9ca3af]">No data</p>}
            </div>
          </div>

          {/* Chart */}
          {(data.chart_data || []).length > 0 && (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5 mb-6" data-testid="profit-chart">
              <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4">Top Products by Gross Profit</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.chart_data} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#1a1a2e' }} width={75} />
                    <Tooltip formatter={v => [fmtRs(v), 'Gross Profit']} />
                    <Bar dataKey="gross_profit" radius={[0, 4, 4, 0]}>
                      {(data.chart_data || []).map((entry, i) => (
                        <Cell key={i} fill={entry.gross_profit >= 0 ? '#1E40AF' : '#EF4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#6b7280]">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left min-w-[600px]" data-testid="profitability-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4">Product Name</th>
                      <th className="py-3 px-4">Category</th>
                      <SortHeader label="Units Sold" col="units_sold" align="text-right" />
                      <SortHeader label="Revenue (Rs.)" col="revenue" align="text-right" />
                      <SortHeader label="COGS (Rs.)" col="cogs" align="text-right" />
                      <SortHeader label="Gross Profit (Rs.)" col="gross_profit" align="text-right" />
                      <SortHeader label="Margin %" col="margin" align="text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, i) => (
                      <tr key={i} className={`border-b border-[#e5e7eb] hover:bg-[#f9fafb] ${tierColor(row.tier)}`}>
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">{row.name}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{row.category}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{row.units_sold}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.revenue)}</td>
                        <td className="py-3 px-4 text-right text-[#6b7280]">{fmtRs(row.cogs)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${row.gross_profit >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]'}`}>{fmtRs(row.gross_profit)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${row.margin >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]'}`}>{row.margin}%</td>
                      </tr>
                    ))}
                    {sortedRows.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-[#9ca3af]">No sales data found for the selected period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profitability;
